import { createSelector } from 'reselect';
import { toPercentage, toSignificant } from '@/utilities/formatting';
import { get } from 'lodash';
import { portfolioSelector } from '@/components/DeprecatedPortfolio/redux/selectors';
import {
  getMarketAverageNetIncomeGrowthAnnualSelector,
  getMarketAverageRevenueGrowthAnnualSelector,
  getMarketAverageCountryNameSelector,
} from '@/components/IndustryAverages/redux/selectors';

export interface Future {
  portfolioId: string;
  portfolioName: string;
  secretCode?: string;
  marketName: string;
  expectedAnnualEarningsGrowthRate?: number;
  pastAverageEarningsGrowthRate?: number;
  marketEarningsGrowthRate?: number;
  expectedRevenueGrowthRate?: number;
  pastAverageRevenueGrowthRate?: number;
  marketRevenueGrowthRate?: number;
  expectedAnnualEarningsGrowthRateFormatted: string;
  pastAverageEarningsGrowthRateFormatted: string;
  marketEarningsGrowthRateFormatted: string;
  expectedRevenueGrowthRateFormatted: string;
  pastAverageRevenueGrowthRateFormatted: string;
  marketRevenueGrowthRateFormatted: string;
}

const formatter = (value?: number | null) =>
  value !== null && value !== undefined ? toPercentage(value / 100, 1) : 'n/a';

export const getFutureSelector = createSelector(
  [
    portfolioSelector,
    getMarketAverageNetIncomeGrowthAnnualSelector,
    getMarketAverageRevenueGrowthAnnualSelector,
    getMarketAverageCountryNameSelector,
  ],
  (
    portfolio,
    marketAverageNetIncomeGrowthAnnual,
    marketAverageRevenueGrowthAnnual,
    marketName
  ): Future => {
    const expectedAnnualEarningsGrowthRate =
      portfolio && portfolio.netIncomeGrowthAnnual
        ? parseFloat(
            toSignificant(portfolio && portfolio.netIncomeGrowthAnnual * 100, 1)
          )
        : undefined;
    const pastAverageEarningsGrowthRate =
      portfolio && portfolio.netIncomeGrowth5y
        ? parseFloat(
            toSignificant(portfolio && portfolio.netIncomeGrowth5y * 100, 1)
          )
        : undefined;
    const marketEarningsGrowthRate =
      marketAverageNetIncomeGrowthAnnual &&
      marketAverageNetIncomeGrowthAnnual.value
        ? parseFloat(
            toSignificant(marketAverageNetIncomeGrowthAnnual.value * 100, 1)
          )
        : undefined;
    const expectedRevenueGrowthRate =
      portfolio && portfolio.revenueGrowthAnnual
        ? parseFloat(
            toSignificant(portfolio && portfolio.revenueGrowthAnnual * 100, 1)
          )
        : undefined;
    const pastAverageRevenueGrowthRate =
      portfolio && portfolio.revenueGrowth5y
        ? parseFloat(
            toSignificant(portfolio && portfolio.revenueGrowth5y * 100, 1)
          )
        : undefined;
    const marketRevenueGrowthRate =
      marketAverageRevenueGrowthAnnual && marketAverageRevenueGrowthAnnual.value
        ? parseFloat(
            toSignificant(marketAverageRevenueGrowthAnnual.value * 100, 1)
          )
        : undefined;

    const expectedAnnualEarningsGrowthRateFormatted = formatter(
      expectedAnnualEarningsGrowthRate
    );
    const pastAverageEarningsGrowthRateFormatted = formatter(
      pastAverageEarningsGrowthRate
    );
    const marketEarningsGrowthRateFormatted = formatter(
      marketEarningsGrowthRate
    );
    const expectedRevenueGrowthRateFormatted = formatter(
      expectedRevenueGrowthRate
    );
    const pastAverageRevenueGrowthRateFormatted = formatter(
      pastAverageRevenueGrowthRate
    );
    const marketRevenueGrowthRateFormatted = formatter(marketRevenueGrowthRate);

    return {
      portfolioId: get(portfolio || {}, ['id'], ''),
      portfolioName: get(portfolio || {}, ['name'], ''),
      secretCode: get(portfolio || {}, ['secretCode'], undefined),
      expectedAnnualEarningsGrowthRateFormatted,
      pastAverageEarningsGrowthRateFormatted,
      marketEarningsGrowthRateFormatted,
      expectedRevenueGrowthRateFormatted,
      pastAverageRevenueGrowthRateFormatted,
      marketRevenueGrowthRateFormatted,
      marketName,
      expectedAnnualEarningsGrowthRate,
      pastAverageEarningsGrowthRate,
      marketEarningsGrowthRate,
      expectedRevenueGrowthRate,
      pastAverageRevenueGrowthRate,
      marketRevenueGrowthRate,
    };
  }
);
